import {
  ForkKnifeIcon,
  HouseIcon,
  LightningIcon,
  WalletIcon,
  BriefcaseIcon,
  GlobeIcon,
  FlaskIcon,
} from '@phosphor-icons/react';
import SectionReveal from "./SectionReveal";
import { foreground, muted, accent, border, secondary, display, body } from "./tokens";

type Category = "Food" | "Housing" | "Utilities" | "Transport" | "Benefits" | "Employment";

const categoryIcon: Record<Category, typeof ForkKnifeIcon> = {
  Food: ForkKnifeIcon,
  Housing: HouseIcon,
  Utilities: LightningIcon,
  Transport: GlobeIcon,
  Benefits: WalletIcon,
  Employment: BriefcaseIcon,
};

const cbos: { name: string; category: Category; members: number }[] = [
  { name: "SNAP (Supplemental Nutrition Assistance)", category: "Benefits", members: 142 },
  { name: "LIHEAP \u2014 Heat & Utility Assistance", category: "Utilities", members: 98 },
  { name: "Gleaners Community Food Bank", category: "Food", members: 87 },
  { name: "Forgotten Harvest", category: "Food", members: 71 },
  { name: "Medicare Savings Program", category: "Benefits", members: 64 },
  { name: "MDOT / SMART Ride Connect", category: "Transport", members: 58 },
  { name: "Salvation Army Rental Assistance", category: "Housing", members: 49 },
  { name: "DTE Low-Income Self-Sufficiency Plan", category: "Utilities", members: 44 },
  { name: "Meals on Wheels", category: "Food", members: 39 },
  { name: "WIC", category: "Benefits", members: 32 },
];

import type { ReportHostedSectionProps } from '../reportHostedSection.types';

const ConnectionsSection = (_props: ReportHostedSectionProps) => {
  const maxMembers = Math.max(...cbos.map((c) => c.members));

  return (
    <section
      id="connections"
      data-pdf-section
      className="section--white report-hosted-section-body"
      style={{ background: "var(--white)", fontFamily: body }}
    >
      <div className="max-w-5xl mx-auto">
        <SectionReveal>
          <div style={{ marginBottom: "2.5rem" }}>
            <h3
              style={{
                fontFamily: display,
                fontWeight: 600,
                fontSize: "var(--reema-text-h2)",
                color: foreground,
                margin: "0 0 0.75rem",
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
              }}
            >
              Where members were sent.
            </h3>
            <p
              style={{
                fontFamily: body,
                fontSize: "var(--reema-text-body-lg)",
                color: muted,
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              We connect members to a broad network of community organizations, government programs, and plan benefits {"\u2014"} ensuring needs are matched with the right resource.
            </p>
          </div>
        </SectionReveal>

        <SectionReveal delay={0.1}>
          <div className="rounded-2xl p-6 md:p-8 shadow-sm" style={{ background: "#fff", border: `1px solid ${border}` }}>
            <div className="flex items-start justify-between mb-1 gap-3">
              <h3 className="text-lg font-semibold" style={{ color: foreground, fontFamily: "'Lora', serif" }}>
                Top CBO & Government Program Connections
              </h3>
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 uppercase shrink-0"
                style={{
                  color: accent,
                  border: `1px solid ${accent}`,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                }}
              >
                <FlaskIcon className="w-3 h-3" />
                Illustrative Data
              </span>
            </div>
            <p className="text-sm mb-6" style={{ color: muted }}>
              Ranked by members connected over the last 6 months.
            </p>
            <ol className="grid sm:grid-cols-2 sm:grid-flow-col sm:grid-rows-5 gap-x-10 gap-y-3">
              {cbos.map((c, i) => {
                const Icon = categoryIcon[c.category];
                const pct = (c.members / maxMembers) * 100;
                return (
                  <li key={c.name} className="flex items-center gap-3">
                    <span className="text-xs tabular-nums w-5 shrink-0" style={{ color: muted }}>
                      {i + 1}.
                    </span>
                    <span
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: secondary, color: accent }}
                    >
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm truncate" style={{ color: foreground }}>
                          {c.name}
                        </span>
                        <span className="text-sm font-semibold tabular-nums" style={{ color: foreground }}>
                          {c.members}
                        </span>
                      </div>
                      <div
                        className="mt-1 h-1.5 rounded-full overflow-hidden"
                        style={{ background: border }}
                      >
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </SectionReveal>
      </div>
    </section>
  );
};

export default ConnectionsSection;
